import React, { FC, useEffect, useRef } from 'react';
import { css } from 'emotion';
import Color from 'color';
import Tracking from '@vromo/analytics';
import YouTubePlayer from 'youtube-player';
import useIntersectionObserver from '@vromo/hooks/useIntersectionObserver';
import Promotion from './widgets/Promotion/Promotion';
import OptIn from '../WeDispatch/widgets/OptIn';
import Rating from '../WeDispatch/widgets/Rating';
import { Theme } from '@emotion/react';
import { Job, Vehicle, Widget as WidgetClass } from '@vromo/models';
import AddressCorrection from '../WeDispatch/widgets/AddressCorrection';
import { TipWidget } from './widgets/Tip/TipWidget';

interface WidgetProps {
    widget: WidgetClass;
    theme: Theme;
    vehicle: Vehicle;
    job: Job;
    host: string;
    auth: string;
}

const Widget: FC<WidgetProps> = ({ widget, theme, vehicle, job, host, auth }) => {
    const ref = useIntersectionObserver(
        ([{ isIntersecting }]) => {
            if (isIntersecting) {
                Tracking.track('Widget Became Visible', { widget: widget.id, type: widget.type });
            }
        },
        { threshold: 1 }
    );
    const youtubeContainerRef = useRef();

    useEffect(() => {
        if (widget.type === 'video' && youtubeContainerRef.current) {
            const player = YouTubePlayer(youtubeContainerRef.current, {
                videoId: widget.content,
                playerVars: { rel: 0 }
            });

            player.on('stateChange', (e) => {
                if (e.data === 1) {
                    Tracking.track('Video Widget Playing', {
                        widget: widget.id,
                        video: widget.content,
                        type: widget.type
                    });
                }
            });
        }
    }, []);

    switch (widget.type) {
        case 'text':
            return (
                <div
                    ref={ref}
                    className={css`
                        font-size: 22px;
                        text-align: left;
                        color: ${Color(theme.background).isLight() ? '#111111' : '#EFEFEF'};
                        font-weight: bold;
                        padding: 12px 24px;
                        margin-bottom: 8px;
                    `}>
                    {widget.content}
                </div>
            );
        case 'promotion':
            return (
                <Promotion ref={ref} vehicle={vehicle} widget={widget} job={job} theme={theme} />
            );
        case 'opt_in':
            return (
                <OptIn
                    ref={ref}
                    vehicle={vehicle}
                    widget={widget}
                    job={job}
                    theme={theme}
                    host={host}
                />
            );
        case 'rating':
            return (
                <Rating
                    ref={ref}
                    vehicle={vehicle}
                    widget={widget}
                    job={job}
                    theme={theme}
                    auth={auth}
                    host={host}
                />
            );
        case 'video':
            return (
                <div ref={ref} style={{ marginBottom: 8 }}>
                    <div
                        ref={youtubeContainerRef}
                        style={{
                            width: '100%',
                            height: 300,
                            overflow: 'hidden'
                        }}
                    />
                </div>
            );
        case 'image':
            return (
                <img
                    ref={ref}
                    src={widget.content}
                    style={{ width: '100%', marginBottom: 8 }}
                    onClick={() =>
                        Tracking.track('Image Widget Clicked', { widget: widget.id, type: 'image' })
                    }
                />
            );
        case 'tip':
            return <TipWidget ref={ref} vehicle={vehicle} theme={theme} />;

        case 'address_correction':
            return (
                <AddressCorrection
                    ref={ref}
                    vehicle={vehicle}
                    widget={widget}
                    job={job}
                    theme={theme}
                    auth={auth}
                    host={host}
                />
            );
        default:
            return null;
    }
};

export default Widget;
